import React from 'react';

import BufferOptionsItem from 'buffers/BufferOptionsItem';
import {CircularSequenceOptionsProps} from 'molbio/maps/options/CircularSequenceOptionsView';
import CreatePDFButton from 'molbio/maps/options/CreatePDFButton';
import HiddenAnnotationsWarningBadge from 'molbio/maps/options/HiddenAnnotationsWarningBadge';
import SearchButton from 'molbio/maps/options/SearchButton';
import SequenceOptionsBanner from 'molbio/maps/options/SequenceOptionsBanner';
import {PanSlider, ZoomSlider} from 'molbio/maps/options/Sliders';
import useCollapsibleNavBar from 'molbio/maps/options/useCollapsibleNavBar';
import {shouldShowHiddenAnnotationsWarning} from 'molbio/maps/options/utils';
import DisplayOptions from 'settings/visibility-options/DisplayOptions';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import ButtonGroup from 'ui-library/buttons/ButtonGroup';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

enum ElementKey {
  PDF = 'pdf',
  SEARCH = 'search',
  WARNING = 'warning',
}

function CircularOptions({
  bannerText,
  onOpenPreview,
  onToggleSearch,
  visibilityOptions,
  featureCounts,
  panSliderProps,
  zoomSliderProps,
  shownAnnotationsCount,
  visibleAnnotationsCount,
  sequenceId,
}: CircularSequenceOptionsProps): JSX.Element {
  let uncollapsibleWidth = 258; // Two sliders (110 each) + visibility options (38)
  const showAnnotationsWarning = shouldShowHiddenAnnotationsWarning({
    shownAnnotationsCount,
    visibleAnnotationsCount,
  });
  if (showAnnotationsWarning) {
    uncollapsibleWidth += 36; // Icon of warning badge
  }
  if (bannerText != null) {
    uncollapsibleWidth += 10; // The banner text collapses, but its margin does not
  }
  const {ref, hiddenStatusByElementKey} = useCollapsibleNavBar<ElementKey>({
    uncollapsibleWidth,
    dropdownWidth: 24,
    collapsibleElements: [
      {key: ElementKey.PDF, width: 92, priority: 2},
      {key: ElementKey.SEARCH, width: 24, priority: 1},
      ...(showAnnotationsWarning
        ? [
            {
              key: ElementKey.WARNING,
              width: 69, // Width of badge with text (105) - badge without text (36)
              priority: 3,
            },
          ]
        : []),
    ],
  });

  const collapsedButtonMenuItems = [
    <Option onSelect={onOpenPreview} key={ElementKey.PDF}>
      Create PDF
    </Option>,
    <Option onSelect={onToggleSearch} key={ElementKey.SEARCH}>
      Search
    </Option>,
  ].filter((item) => hiddenStatusByElementKey[item.key as ElementKey]);

  return (
    <div className="buffer-options-container" ref={ref}>
      <BufferOptionsItem>
        <ZoomSlider {...zoomSliderProps} />
      </BufferOptionsItem>
      <BufferOptionsItem>
        <PanSlider {...panSliderProps} />
      </BufferOptionsItem>
      {showAnnotationsWarning && (
        <HiddenAnnotationsWarningBadge iconOnly={hiddenStatusByElementKey[ElementKey.WARNING]} />
      )}
      <SequenceOptionsBanner overrideBannerText={bannerText} sequenceId={sequenceId} />
      <BufferOptionsItem className="u-vertical-center-items">
        <ButtonGroup>
          {!hiddenStatusByElementKey[ElementKey.PDF] && <CreatePDFButton onOpenPreview={onOpenPreview} />}
        </ButtonGroup>
        <ButtonGroup>
          {collapsedButtonMenuItems.length > 0 && (
            <Dropdown menu={<Menu width={MenuWidth.SMALL}>{collapsedButtonMenuItems}</Menu>}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.MINI}
                isDropdown={true}
                hideDropdownCaret={true}
                icon="ellipsis-vertical"
              />
            </Dropdown>
          )}
        </ButtonGroup>
        <ButtonGroup>
          <DisplayOptions dropLeft={true} options={visibilityOptions} featureCounts={featureCounts} />
          {!hiddenStatusByElementKey[ElementKey.SEARCH] && <SearchButton onToggleSearch={onToggleSearch} />}
        </ButtonGroup>
      </BufferOptionsItem>
    </div>
  );
}

export default CircularOptions;
